import React, { useState, useEffect, useRef } from "react";
import { gl_start, registerSceneHMR } from "./webgl0.js";

function Scene() {
  this.vertexShader = `#version 300 es
  in vec3 aPos;
  out vec3 vPos;

  void main() {
    gl_Position = vec4(aPos, 1.0);
    vPos = aPos;
  }`;

  this.fragmentShader = `#version 300 es
  precision mediump float;

  in vec3 vPos;
  out vec4 fragColor;

  void main() {
    vec3 p = vec3(vPos.xy, sqrt(1.0 - dot(vPos, vPos)));

    fragColor = vec4(0.0);
    if(p.z > 0.0) fragColor = vec4(1.0);

    fragColor = vec4(1.0 - p.z, 1.0 - p.z, 1.0 - p.z, 1.0);
  }`;
}

export default function WebGLCanvas() {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [scene, setScene] = useState(null);

  const hoverRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    left: 0,
    top: 0,
    fragCoord: [0,0],
    fragCoordNorm: [0,0],
    color: 'rgba(255,255,255,1)',
    colorNorm: [1,1,1,1]
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!canvas || !overlay) return;

    try { if (typeof canvas.stopRender === 'function') canvas.stopRender(); } catch (e) {}

    const newScene = new Scene();
    gl_start(canvas, newScene);
    setScene(newScene);

    try { registerSceneHMR(module, canvas, () => new Scene()); } catch (e) {}

    // resize overlay to match canvas backing buffer
    const resizeOverlay = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      overlay.width = rect.width * dpr;
      overlay.height = rect.height * dpr;
      overlay.style.width = rect.width + "px";
      overlay.style.height = rect.height + "px";
    };
    resizeOverlay();
    window.addEventListener("resize", resizeOverlay);

    const handleMouseMove = (e) => {
      const rect = overlay.getBoundingClientRect();
      const scaleX = overlay.width / rect.width;
      const scaleY = overlay.height / rect.height;

      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;

      // NDC coordinates (-1 to 1)
      const x = (mouseX / overlay.width) * 2 - 1;
      const y = -((mouseY / overlay.height) * 2 - 1);
      let z = 0.0;

      const xy2 = x*x + y*y;
      if (xy2 <= 1.0) z = Math.sqrt(1 - xy2);

      let r = (255 - (z * 255)).toFixed(1);
      const color = `rgba(${ r }, ${ r }, ${ r }, 255)`;
      const colorNorm = [ 1 - z, 1 - z, 1 - z,1];

      // update tooltip
      setTooltip({
        visible: true,
        left: e.clientX + 10,
        top: e.clientY + 10,
        fragCoord: [mouseX.toFixed(1), mouseY.toFixed(1)],
        fragCoordNorm: [x, y],
        color,
        colorNorm
      });

      // update hover position for crosshair
      hoverRef.current = { x: mouseX, y: mouseY };
    };

    const handleMouseOut = () => {
      setTooltip(t => ({ ...t, visible: false }));
      hoverRef.current = null;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseout', handleMouseOut);

    // animation frame for overlay crosshair
    let rafId;
    const drawOverlay = () => {
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);

     if (hoverRef.current) {
        const px = hoverRef.current.x;
        const py = hoverRef.current.y; // top-left origin for 2D canvas

        ctx.save();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';

        // horizontal line
        ctx.beginPath();
        ctx.moveTo(0, py + 0.5);
        ctx.lineTo(overlay.width, py + 0.5);
        ctx.stroke();

        // vertical line
        ctx.beginPath();
        ctx.moveTo(px + 0.5, 0);
        ctx.lineTo(px + 0.5, overlay.height);
        ctx.stroke();

        ctx.restore();
        }


      rafId = requestAnimationFrame(drawOverlay);
    };
    rafId = requestAnimationFrame(drawOverlay);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener("resize", resizeOverlay);
      if (rafId) cancelAnimationFrame(rafId);
      try { if (typeof canvas.stopRender === 'function') canvas.stopRender(); } catch (e) {}
    };
  }, []);

  return (
<div style={{ 
  display: "flex", 
  flexDirection: "row", 
  justifyContent: "center", 
  gap: "16px",
  width: "100vw",
  marginLeft: `calc(-50vw + 360px)` // 360 = half of your canvas+panel width
}}>
<div style={{ position: "relative", width: "400px", height: "400px", background: "blue", display : "block" }}>
  <canvas
    ref={canvasRef}
    width={400}
    height={400}
    style={{ width: "100%", height: "100%", display: "block", background: "black" }}
  />
  <canvas
    ref={overlayRef}
    width={400}
    height={400}
    style={{
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      position: "absolute",
      left: 0,
      top: 0,
    }}
  />
</div>


  <pre>{scene?.fragmentShader}</pre>
  

   {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.left,
            top: tooltip.top,
            background: 'rgba(255,255,255,0.95)',
            color: '#000',
            padding: '6px 8px',
            borderRadius: 6,
            pointerEvents: 'none',
            fontSize: 12,
            zIndex: 9999,
            border: "1px solid rgba(0, 0, 0, 0.04)"
          }}
        >
          <div style={{ display: 'flex', flexDirection: "column", gap: "8px" }}>

            <div style={{ width: 24, height: 24, background: tooltip.color, border: '1px solid #ddd', marginRight: 8 }} />

            <div>
              <p className="meta">Position</p>
              <div>( {tooltip.fragCoordNorm[0].toFixed(1)}, {tooltip.fragCoordNorm[1].toFixed(1)} )</div>
              <div style={{ color: 'var(--black40)' }}>( {tooltip.fragCoord[0]}, {tooltip.fragCoord[1]} )</div>
            </div>

            <div>
              <p className="meta">Color</p>
              <div>( {tooltip.colorNorm[0].toFixed(1)}, {tooltip.colorNorm[1].toFixed(1)}, {tooltip.colorNorm[2].toFixed(1)}, {tooltip.colorNorm[3].toFixed(1)} )</div>
              <div style={{ color: 'var(--black40)' }}>{tooltip.color.slice(4)}</div>
            </div>
          </div>
        </div>
      )}
      
</div>
  );
}
